import { api } from './client';

/**
 * Refer and earn.
 *
 * ---------------------------------------------------------------------------
 * THE CODE COMES FROM THE SERVER, NOT FROM THE PHONE NUMBER
 * ---------------------------------------------------------------------------
 * `data/referral.ts` derived a code from the customer's phone locally. That was
 * a stand-in and is not what the backend issues: codes live in `ReferralCode`,
 * are unique, and can be BLOCKED by ops for abuse. A locally-derived code would
 * be shared, typed in by a friend, and rejected - with the referrer never
 * seeing why. `summary().code` is the only code that exists.
 *
 * ---------------------------------------------------------------------------
 * REWARDS ARE PAID IN COINS, AND ONLY ONCE A BOOKING QUALIFIES
 * ---------------------------------------------------------------------------
 * Signing up pays nothing. The reward lands when the friend completes a
 * qualifying booking inside `qualifyWindowDays`, which is why `status` has
 * three values and why `pendingCount` exists - those are referrals that joined
 * but have not yet earned anything. Presenting them as paid is the one lie this
 * screen must not tell.
 */

/**
 * How far one referral has got.
 *
 *   pending    joined, no qualifying booking yet - nothing paid
 *   qualified  the booking landed, reward owed
 *   rewarded   coins are in the wallet
 *   rejected   ops refused it (duplicate account, abuse)
 *   expired    the qualify window closed first
 *
 * Typed as a union for the known values but widened to `string`, because the
 * backend column is a free string and an unrecognised status must render as
 * itself rather than crash a `Record` lookup.
 */
export type ReferralStatus =
  | 'pending'
  | 'qualified'
  | 'rewarded'
  | 'rejected'
  | 'expired'
  | (string & {});

/** What `GET /customers/me/referrals` answers. */
export type ReferralSummary = {
  /** The real, server-issued code. Share this and nothing else. */
  code: string;
  /** Pre-written share text from the backend, code already embedded. */
  shareMessage: string;
  /** Coins the referrer gets per qualifying referral. */
  referrerCoins: number;
  /** Coins the friend gets. */
  refereeCoins: number;
  /** How long the friend has to book before the referral lapses. */
  qualifyWindowDays: number;
  totalReferrals: number;
  /** How many actually paid out. Never equal to `totalReferrals` by default. */
  qualifiedCount: number;
  totalCoinsEarned: number;
  /** Joined but not yet earning. Show these as in-progress, never as paid. */
  pendingCount: number;
  /**
   * Ops has blocked this code for abuse.
   *
   * The share button must be disabled when true: a blocked code still looks
   * valid and every friend who types it in will be refused.
   */
  isBlocked: boolean;
};

/** One referral, in either direction. */
export type Referral = {
  id: string;
  code: string;
  status: ReferralStatus;
  referrerCoins: number;
  refereeCoins: number;
  /** ISO date-times, null until each step happens. */
  qualifiedAt: string | null;
  rewardedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
};

/** My code, the reward policy, and how my referrals are doing. */
export const summary = (signal?: AbortSignal): Promise<ReferralSummary> =>
  api.get<ReferralSummary>('/customers/me/referrals', { signal });

/** People I referred. */
export const sent = (signal?: AbortSignal): Promise<Referral[]> =>
  api.get<Referral[]>('/customers/me/referrals/sent', { signal });

/**
 * The referral I arrived through, if any.
 *
 * Null for a customer who signed up on their own - the common case, and not an
 * error.
 */
export const received = (signal?: AbortSignal): Promise<Referral | null> =>
  api.get<Referral | null>('/customers/me/referrals/received', { signal });

/**
 * Redeem somebody else's code.
 *
 * Throws `ApiError` like every other service call. The failures worth showing
 * distinctly are 409 (already referred, or your own code) and 404 (no such
 * code) - both are the customer's mistake and both have a clear sentence.
 */
export const apply = (code: string): Promise<Referral> =>
  api.post<Referral>('/customers/me/referrals/apply', { code });

/**
 * A status, as a sentence for the customer.
 *
 * The backend's `REFERRAL_STATUSES`, not the mock's three (`joined` / `booked`
 * / `paid`) which never matched the server. `pending` and `qualified` are the
 * distinction that matters most: a friend who signed up has earned nobody
 * anything yet, and saying otherwise is the one promise this screen must not
 * break.
 *
 * A lookup, not a `Record`, so an unrecognised status from a newer backend
 * renders as itself instead of crashing the row.
 */
const STATUS_LABELS: Record<string, string> = {
  pending: 'Signed up - no booking yet',
  qualified: 'Booked - reward on its way',
  rewarded: 'Reward credited',
  rejected: 'Not eligible',
  expired: 'Expired before booking',
};

export const statusLabel = (status: ReferralStatus): string =>
  STATUS_LABELS[status] ?? status;
